import { useCallback, useState } from 'react'
import { checkVoucher } from '../../utils/api'
import { errorMessage } from '../../core/errors'
import { codeLast4, normalizeVoucherCode, redeemCents } from '../../core/rules/ledger'
import { useSubmit } from '../../hooks/useSubmit'

export type AppliedVoucher = { code: string; balanceCents: number; validUntil: string }

/**
 * Het veld "Cadeaubon" bij afrekenen. Controleren boekt niets af (POST /vouchers/check); dat
 * gebeurt pas bij plaatsen, door de server. Wat er afgaat is nooit meer dan het totaal.
 */
export function useVoucher(totalCents: number) {
  const [input, setInput] = useState('')
  const [applied, setApplied] = useState<AppliedVoucher | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const check = useSubmit(checkVoucher)

  const apply = useCallback(async () => {
    setProblem(null)
    const code = normalizeVoucherCode(input)
    if (!code) {
      setProblem('Dit lijkt geen cadeauboncode. Hij staat op de bon, bijvoorbeeld BB-7K3Q-M9TX-4HPA.')
      return
    }
    const r = await check.submit({ code })
    if (r) {
      setApplied({ code, balanceCents: r.balanceCents, validUntil: r.validUntil })
      setInput('')
    }
  }, [input, check])

  const remove = useCallback(() => {
    setApplied(null)
    setProblem(null)
    check.reset()
  }, [check])

  /** De bestelling weigerde de bon (intussen gebruikt of verlopen): weghalen en zeggen waarom. */
  const reject = useCallback(
    (err: unknown) => {
      setApplied(null)
      check.reset()
      setProblem(errorMessage(err) ?? 'Deze cadeaubon kan nu niet gebruikt worden.')
    },
    [check],
  )

  const cents = applied ? redeemCents(applied.balanceCents, totalCents) : 0
  return {
    input,
    setInput,
    applied,
    last4: applied ? codeLast4(applied.code) : null,
    cents,
    /** Wat er na de bon overblijft. */
    payableCents: totalCents - cents,
    /** Wat er na deze bestelling op de bon overblijft. */
    leftCents: applied ? applied.balanceCents - cents : 0,
    apply,
    remove,
    reject,
    checking: check.submitting,
    error: problem ?? check.error,
  }
}

export type VoucherControl = ReturnType<typeof useVoucher>
